import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Redirect, useHistory, useParams } from "react-router-dom";

// import CurrencyInput from "react-currency-input-field";
import styled from "styled-components/macro";

import TierPriceEditor from "Payments/TierPricing/TierPriceEditor";
import API from "Signups/API";
import { COST_TYPES } from "Signups/SharedComponents/costTypes";
import { getVerbs } from "Signups/SharedComponents/signupListVerbs";
import { useIsSignupClosed } from "Signups/SharedComponents/useIsSignupClosed";
import { useNewSignupCreationFlow } from "Signups/SignupView/NewSignup";
import { useRefreshSignupListsData } from "Signups/SignupView/SignupSheet/useRefreshSignupListsData";
import {
  getFundItAmountsInCents,
  getFundItAmountsInDollars,
  getFundItAmountsPerItem,
  getTiersHaveChanged,
} from "Signups/SignupView/SignupSheet/utils/fundItAmountsConversions";
import { useSignups } from "Signups/state";
import toTwoDecimals from "Signups/utils/toTwoDecimals";
import { AnimatePresence, motion } from "framer-motion";

import FnrButton, { ButtonText } from "UI/FnrButton3";
import FnrCheckbox from "UI/FnrEnrichedCheckbox/FnrCheckbox";
import FnrFade from "UI/FnrFade/FnrFade";
import { FnrPopover } from "UI/FnrPopover2";
import { ExpandingRadioButtonOption } from "UI/FnrRadioButtonOption/ExpandingRadioButtonOption";
import { colors } from "UI/colors";

import useComponentIsMounted from "Utilities/Hooks/useComponentIsMounted";

import { useSignupItem } from "../../EditSignupItemNav/statehooks";

import AmountRow from "./AmountRow";

export default function FundIt() {
  const [item, setSignupItem] = useSignupItem();
  const { signupItemTab, signupItemID } = useParams();
  const [
    {
      currentSignupList: {
        ID: signupID,
        hasDisabledFundItViaItem,
        isPerSignupCostType,
        signupItems,
        signupListTypeID,
        fundIt: { isSuggestedDonation, fund } = {},
      },
    },
    dispatch,
  ] = useSignups();

  const [tiersValid, setTiersValid] = useState(true);
  const ID = Number(signupItemID);
  const hasSignupCost = item?.fundIt?.hasSignupCost;
  const costType = item?.fundIt?.cost?.costType;

  const initialAmounts = useMemo(() => {
    if (!hasSignupCost || !costType) return defaultAmounts;
    return getFundItAmountsInDollars(item?.fundIt?.cost?.amounts);
  }, [costType, hasSignupCost, item?.fundIt?.cost?.amounts]);

  const initialCostType = useMemo(
    () => (hasSignupCost && costType ? costType : defaultCostType),
    [costType, hasSignupCost]
  );

  const { isInCreationFlow } = useNewSignupCreationFlow();
  const history = useHistory();

  const refreshSignupListsData = useRefreshSignupListsData();

  const { isArchived } = useIsSignupClosed();

  const [isSaving, setIsSaving] = useState(false);
  const [stagedValues, setStagedValues] = useState({
    hasSignupCost,
    amounts: initialAmounts,
    costType: initialCostType,
  });
  const [initialStagedValues, setInitialStagedValues] = useState({
    hasSignupCost,
    amounts: initialAmounts,
    costType: initialCostType,
  });

  useEffect(() => {
    /**
     * This effect makes sure that the initialStagedValues are updated after the item has been passed.
     * It gets passed as null before it has value.
     */
    if (!hasSignupCost) return;

    setInitialStagedValues({
      hasSignupCost,
      amounts: initialAmounts,
      costType: initialCostType,
    });
  }, [hasSignupCost, initialAmounts, initialCostType]);

  function clearTierUpdates() {
    setStagedValues(initialStagedValues);
  }

  const [preventTierAnimation, setPreventTierAnimation] = useState(false);

  const amountsHaveChanged = getTiersHaveChanged(
    stagedValues?.amounts,
    item?.fundIt?.cost?.amounts,
    stagedValues?.costType === "PerSlot",
    hasSignupCost
  );

  const [syncedItemId, setSyncedItemId] = useState(null);

  useEffect(
    function syncStagedValuesToProps() {
      if (syncedItemId === item?.ID || (!item?.ID && !syncedItemId)) return;

      setStagedValues({
        hasSignupCost,
        amounts: initialAmounts,
        costType: initialCostType,
      });

      setSyncedItemId(item.ID);
    },
    [hasSignupCost, initialAmounts, initialCostType, syncedItemId, item]
  );

  const hasNoValue = stagedValues?.amounts?.some(
    (amount) => amount?.amount <= 0
  );

  const [amountValidationError, setAmountValidationError] = useState("");
  const [isShowingConfirmation, setIsShowingConfirmation] = useState(false);
  const saveButton = useRef();

  const amountInputRef = useRef();

  const isMounted = useComponentIsMounted();

  useEffect(() => {
    if (stagedValues.hasSignupCost)
      setTimeout(amountInputRef.current?.focus, 300);
  }, [stagedValues.hasSignupCost]);

  const hasFund = fund?.ID > 0;

  const signupLevelFundItPath = `/signups/${signupID}/fund-it`;

  const amountsInAllowableRange = stagedValues?.amounts?.every((a) => {
    const amount = Number(toTwoDecimals(a.amount));
    return 2 <= amount && amount <= 10000;
  });

  const toggleFundIt = () => {
    const isTogglingOn = !stagedValues.hasSignupCost;

    setStagedValues({
      hasSignupCost: isTogglingOn,
      amounts: initialAmounts,
      costType: initialCostType,
    });
  };

  const hasSignupPaymentsCollected = Boolean(fund?.signupPaymentsCollected);
  /*
   * Users can turn on FundIt for an item (whether new or existing), EVEN if that signup has had payments collected
   *   (i.e. "from free, to paid" is always allowed)
   * However, users CANNOT ALTER ANY FundIt settings for an *existing paid item*,
   *   if any signup payments have been collected
   */
  const isEditingDisabled = (item?.respondents?.length > 0 && hasSignupPaymentsCollected && hasSignupCost)
    || isSaving || isArchived;
  const canChangeCostType = !(hasSignupPaymentsCollected && hasSignupCost);
  const hasOutstandingChanges =
    hasSignupCost !== stagedValues.hasSignupCost ||
    amountsHaveChanged ||
    initialCostType !== stagedValues.costType;

  const isTurningOn = stagedValues.hasSignupCost;
  const isOnlyItemWithFundIt =
    signupItems.filter(({ hasSignupCost }) => hasSignupCost).length === 1;
  const shouldClearFund = !isTurningOn && isOnlyItemWithFundIt;

  const submit = (e) => {
    e.preventDefault();
    if (!tiersValid) return;
    if (shouldClearFund) {
      setIsShowingConfirmation(true);
    } else {
      save();
    }
  };

  const goToAnotherItem = () => {
    return history.push(`/signups/${signupID}/items/another`);
  };

  const save = () => {
    if (isTurningOn && !amountsInAllowableRange) {
      setAmountValidationError(
        "Amount must be at least $2 and less than $10,000"
      );
      return;
    }

    if (!hasOutstandingChanges && isInCreationFlow) {
      // Skip the API call, we have nothing to Save
      return goToAnotherItem();
    }
    const amountsToSave =
      stagedValues.costType === "PerSlot"
        ? getFundItAmountsPerItem(stagedValues?.amounts)
        : getFundItAmountsInCents(stagedValues?.amounts);
    const valuesToSave = {
      signupListItemID: ID,
      hasSignupCost: stagedValues.hasSignupCost,
      amounts: isTurningOn ? amountsToSave : 0,
      costType: isTurningOn ? stagedValues.costType : null,
    };

    setIsSaving(true);

    API.editItemFundItOptions(signupID, valuesToSave)
      .post()
      .then(() => {
        setSignupItem({
          ...item,
          fundIt: {
            ...(item?.fundIt || {}),
            hasSignupCost: valuesToSave.hasSignupCost,
            cost: {
              costType: valuesToSave.costType,
              amounts: amountsToSave,
            },
          },
        });
        if (!isTurningOn) {
          const newStagedValues = {
            hasSignupCost: false,
            amounts: defaultAmounts,
            costType: defaultCostType,
          };
          setStagedValues(newStagedValues);
          setInitialStagedValues(newStagedValues);
        } else {
          setInitialStagedValues(stagedValues);
        }

        // Handle automatic Fund deletion when this is the last item with fund it
        if (!shouldClearFund) return;

        dispatch({
          type: "Update Edit Signup Info",
          key: "hasDisabledFundItViaItem",
          value: true,
        });
        dispatch({
          type: "setSignupFund",
          payload: null,
        });
        // Re-fetch funds in case this fund gets deleted
        refreshSignupListsData();
      })
      .catch((err) => {
        console.error(err);
        alert(
          "Oh no! Something went wrong. Please refresh and try again, or contact help@flocknote.com."
        );
      })
      .finally(() => {
        closeConfirmation();
        setIsSaving(false);

        if (isInCreationFlow) return goToAnotherItem();
      });
  };

  const hasAmountValidationError = amountValidationError.length > 0;

  const { entity } = getVerbs(signupListTypeID);
  const isPerSlotSelected = stagedValues.costType === COST_TYPES.PER_SLOT;
  const radioButtonOptionStyle = {
    cursor: isEditingDisabled ? "not-allowed" : "pointer",
    overflow: "hidden",
  };

  const closeConfirmation = () => {
    setIsShowingConfirmation(false);
  };

  const setTierData = (data) => {
    setStagedValues({
      ...stagedValues,
      amounts: data,
    });
    return data;
  };

  const setPerSlotValue = (val) => {
    let newAmounts = stagedValues?.amounts;
    if (!newAmounts) return;

    newAmounts.find(({ numberOfSlots }) => numberOfSlots === null).amount = val;
    setStagedValues({
      amounts: newAmounts,
      ...stagedValues,
    });
  };

  function handlePreventTierAnimation() {
    if (stagedValues?.costType !== COST_TYPES.PER_ITEM) {
      setTimeout(() => {
        setPreventTierAnimation(false);
      }, 300);
    } else {
      setPreventTierAnimation(true);
    }
  }

  if (signupItemTab === "fund-it" && !hasFund && !hasDisabledFundItViaItem) {
    console.log(12)
    return <Redirect to={`/signups/${signupID}/items/${ID}/info`} />;
  }
  if (!hasFund && hasDisabledFundItViaItem)
    return (
      <Instructions
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        Payments have been disabled for this signup. Please go{" "}
        <Link to={signupLevelFundItPath}>here</Link> to re-enable.
      </Instructions>
    );

  if (isPerSignupCostType)
    return (
      <Instructions
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        This Signup is configured to charge per household. To set the cost on a
        per-item or per-spot basis, you can adjust the settings{" "}
        <Link to={signupLevelFundItPath}>here</Link>.
        <FnrFade
          show={
            isInCreationFlow && !!item // Using item to make sure the data is loaded before showing the save button.
          }
        >
          <ButtonWrapper>
            <FnrButton color="blue" onClick={() => goToAnotherItem()}>
              <ButtonText>Next</ButtonText>
            </FnrButton>
          </ButtonWrapper>
        </FnrFade>
      </Instructions>
    );

  return (
    <Form onSubmit={submit}>
      <FnrCheckbox
        on={Boolean(stagedValues.hasSignupCost)}
        toggleOn={toggleFundIt}
        disabled={isEditingDisabled}
        data-cy="fnrCheckbox"
      >
        {isSuggestedDonation ? "Suggest a donation for" : "Take payments for"}{" "}
        this Item
      </FnrCheckbox>

      <AnimatePresence>
        {stagedValues.hasSignupCost && (
          <FundItOptions
            initial={isMounted ? HIDDEN : null}
            animate={{
              opacity: 1,
              height: "auto",
              y: 0,
            }}
            exit={{
              ...HIDDEN,
              transition: {
                type: "tween",
                duration: 0.3,
                ease: "backIn",
              },
            }}
          >
            {canChangeCostType ? (
              <RadioWrapper>
                <ExpandingRadioButtonOption
                  label={`Per ${entity}`}
                  isActive={isPerSlotSelected}
                  onClick={() => {
                    if (isEditingDisabled) return;

                    handlePreventTierAnimation();
                    setStagedValues({
                      ...stagedValues,
                      costType: COST_TYPES.PER_SLOT,
                    });
                  }}
                  style={{ marginBottom: 8, ...radioButtonOptionStyle }}
                  iconColor={
                    isPerSlotSelected ? colors.fnBlue700 : colors.fnGray400
                  }
                  subLabel={`Each ${entity} someone signs up for will incur its own charge.`}
                >
                  <div style={{ marginTop: "8px" }}>
                    <AmountRow
                      value={
                        stagedValues?.amounts?.find(
                          (amount) => amount?.numberOfSlots === null
                        )?.amount
                      }
                      setValue={setPerSlotValue}
                      amountInputRef={amountInputRef}
                      hasAmountValidationError={hasAmountValidationError}
                      setAmountValidationError={setAmountValidationError}
                      isSaving={isSaving}
                      isArchived={isArchived}
                      amountValidationError={amountValidationError}
                    />
                  </div>
                </ExpandingRadioButtonOption>

                {signupListTypeID !== 4 && ( // don't show for polls
                  <ExpandingRadioButtonOption
                    label="Per household"
                    isActive={!isPerSlotSelected}
                    onClick={() => {
                      if (isEditingDisabled) return;
                      handlePreventTierAnimation();

                      setStagedValues({
                        ...stagedValues,
                        costType: COST_TYPES.PER_ITEM,
                      });
                    }}
                    style={radioButtonOptionStyle}
                    iconColor={
                      !isPerSlotSelected ? colors.fnBlue700 : colors.fnGray400
                    }
                    subLabel="This setting allows you either to charge each household a fixed amount or create pricing tiers."
                  >
                    <TierPriceEditorWrapper>
                      <TierPriceEditor
                        tierData={stagedValues?.amounts}
                        setTierData={setTierData}
                        setTierDataValid={setTiersValid}
                        entity={entity}
                        preventTierAnimation={preventTierAnimation}
                      />
                    </TierPriceEditorWrapper>
                  </ExpandingRadioButtonOption>
                )}
              </RadioWrapper>
            ) : (
              <>
                {isPerSlotSelected && (
                  <AmountRow
                    value={stagedValues.amounts[0].amount}
                    setValue={setPerSlotValue}
                    amountInputRef={amountInputRef}
                    hasAmountValidationError={hasAmountValidationError}
                    setAmountValidationError={setAmountValidationError}
                    isSaving={isSaving}
                    isArchived={isArchived}
                    amountValidationError={amountValidationError}
                  />
                )}
                {!isPerSlotSelected && (
                  <TierPriceEditor
                    tierData={stagedValues?.amounts}
                    setTierData={setTierData}
                    setTierDataValid={setTiersValid}
                    entity={entity}
                    preventTierAnimation={preventTierAnimation}
                  />
                )}
              </>
            )}
          </FundItOptions>
        )}
      </AnimatePresence>
      {
        <TierButtonsWrapper>
          {hasOutstandingChanges && (
            <FnrButton type="button" onClick={clearTierUpdates}>
              <ButtonText>Cancel</ButtonText>
            </FnrButton>
          )}

          <div style={{ marginLeft: "auto" }}>
            <FnrFade
              show={
                (isInCreationFlow || (hasOutstandingChanges && !hasNoValue)) &&
                !!item // Using item to make sure the data is loaded before showing the save button.
              }
            >
              <ButtonWrapper>
                <FnrButton
                  type="submit"
                  color="green"
                  loading={isSaving && !isShowingConfirmation}
                  disabled={isSaving || !tiersValid}
                  ref={saveButton}
                >
                  <ButtonText>Save</ButtonText>
                </FnrButton>
              </ButtonWrapper>
            </FnrFade>
          </div>
        </TierButtonsWrapper>
      }
      <FnrPopover
        show={isShowingConfirmation}
        onShouldClose={closeConfirmation}
        buttonRef={saveButton}
        style={{ width: 250, padding: 10 }}
      >
        <strong>
          Heads up! Disabling Fund It for this item will reset all Fund It
          settings for this signup.
        </strong>
        <ConfirmButtonsWrapper>
          <FnrButton size="small" onClick={closeConfirmation}>
            Cancel
          </FnrButton>

          <FnrButton color="red" size="small" onClick={save} loading={isSaving}>
            Disable Anyway
          </FnrButton>
        </ConfirmButtonsWrapper>
      </FnrPopover>
    </Form>
  );
}

const defaultAmounts = [
  {
    numberOfSlots: null,
    amount: 2,
  },
];
const defaultCostType = COST_TYPES.PER_SLOT;

const HIDDEN = {
  opacity: 0,
  height: 0,
  y: -20,
};

const Form = styled.form`
  padding: 18px 0;
  max-width: 480px;
  font-weight: 400;
  box-sizing: border-box;
`;

const Instructions = styled(motion.div)`
  margin: 1rem;
  font-weight: 400;
  max-width: 450px;
  color: ${colors.fnGray700};

  a {
    color: ${colors.fnBlue700};
  }
`;

const FundItOptions = styled(motion.div)`
  margin-left: 2.2rem;
  margin-top: 1rem;

  @media screen and (max-width: 480px) {
    margin-left: 0;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row-reverse;
`;

const RadioWrapper = styled.div``;

const ConfirmButtonsWrapper = styled.div`
  margin-top: 14px;
  display: flex;
  justify-content: space-between;
`;

const TierButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-left: 61px;
  margin-top: 1.5rem;
`;

const TierPriceEditorWrapper = styled.div`
  cursor: auto;
  padding-top: 16px;

  @media screen and (max-width: 480px) {
    margin-left: -27px;
  }
`;
